import { takeEvery, put, call, select } from "redux-saga/effects"

// Constantes
import {
  PATCH_VALIDATE_ACTION_PLAN,
  VALID_OR_REJECT_TASKS_ACTION_PLAN,
  PATCH_CONFIRM_ACTION_PLAN,
  GET_VALIDATE_ACTION_PLAN,
  // SPECIAL FOR ACTION PLAN THREAD
  GET_ACTION_PLAN_THREAD_BY_ACTION_PLAN_ID,
  POST_MESSAGE_ACTION_PLAN_THREAD,
} from "./constants"

// Sagas
import {
  manageSuccessGenerator,
  responseTypeIsSuccess,
  manageErrorGenerator,
  responseTypeIsError,
} from "Containers/App/saga"

// Actions
import { setErrorMessage } from "Containers/Messages/actions"
import { getAuditPlanByIdAction } from "ContainersForm/AuditPlans/actions.js"
import {
  getFullAnomaliesAction,
  getFullAuditRecommendationsAction,
  getFullIncidentsAction,
} from "ContainersForm/NonConformity/actions.js"
import {
  getValidateActionPlanSuccessAction,
  getValidateActionPlanAction,
  getActionPlanThreadAction,
  getActionPlanThreadSuccessAction,
} from "./actions"

// Selectors
import {
  selectCurrentToken,
  selectCurrentUser,
  selectTranslation,
} from "Containers/Login/selectors"

// Utils
import { getCors, postCors, patchCors } from "Utils/rulesCors"
import { config } from "Utils/config"
import request from "Utils/request"
import { postNotificationAction } from "Containers/Notifications/actions"
import { selectAdminUsers } from "Containers/BusinessInformations/selectors"
import { generateNotification, generateTemplateNotification } from "Components/Notifications/utils"

function* getValidateActionPlanSaga() {
  const token = yield select(selectCurrentToken)

  const specificHeaders = { token: token }
  const { FORM_BACK_URL } = config
  const requestURL = `${FORM_BACK_URL}/actionPlansToValidate `

  try {
    const response = yield call(request, requestURL, getCors(specificHeaders))
    if (responseTypeIsError(response)) {
      yield call(manageErrorGenerator, { response, showMessageToUser: true })
    } else {
      if (responseTypeIsSuccess(response)) {
        yield call(manageSuccessGenerator, { response, showMessageToUser: false })
        yield put(getValidateActionPlanSuccessAction(response.body))
      }
    }
  } catch (err) {
    yield put(setErrorMessage(err))
  }
}

function* patchValidateActionPlanSaga({ payload }) {
  const { nonConformity } = payload
  const { name, type, actionPlanId, creatorId } = nonConformity
  const nonConformityId = nonConformity?._id?.$oid
  const currentUser = yield select(selectCurrentUser)
  const adminUsers = yield select(selectAdminUsers)
  const token = yield select(selectCurrentToken)
  const trad = yield select(selectTranslation)
  const paramsNotif = {
    currentUser: currentUser,
    usersIdsToInform: [...new Set([...adminUsers?.map(user => user?.USER_ID), creatorId])],
    eventString: trad?.VALIDATED_ACTION_PLAN,
    elementInBold: name,
    redirectUrl: `/nonConformity-details/${type}/${nonConformityId}`,
    redirectState: { object: nonConformity },
    channelId: "complication_created",
  }
  const notif = generateNotification(paramsNotif)
  const infos_notif = generateTemplateNotification("action_plan_patch", {
    notif,
    currentUser,
    payload,
    trad,
  })
  const requestURL = `${config?.FORM_BACK_URL}/validateActionPlan/${actionPlanId}`

  try {
    const response = yield call(request, requestURL, patchCors(payload, { token }))
    if (responseTypeIsError(response)) {
      yield call(manageErrorGenerator, { response, showMessageToUser: true })
    } else {
      if (responseTypeIsSuccess(response)) {
        yield call(manageSuccessGenerator, { response, showMessageToUser: true })
        yield put(postNotificationAction({ notif, infos_notif }))
        yield put(getValidateActionPlanAction())
        if (type === "anomaly") {
          yield put(getFullAnomaliesAction())
        } else if (type === "incident") {
          yield put(getFullIncidentsAction())
        } else if (type === "auditRecommendation") {
          yield put(getFullAuditRecommendationsAction())
        }
      }
    }
  } catch (err) {
    yield put(setErrorMessage(err))
  }
}

function* validOrRejectTasksActionPlanSaga({ payload }) {
  const { actionPlanId, action, nonConformityType, auditPlanId } = payload
  const token = yield select(selectCurrentToken)
  const specificHeaders = { token: token }
  const { FORM_BACK_URL } = config
  const endPath = `validOrRejectTasksActionPlan/${actionPlanId}`
  const requestURL = `${FORM_BACK_URL}/${endPath}`

  try {
    const response = yield call(
      request,
      requestURL,
      patchCors({ action, nonConformityType }, specificHeaders)
    )

    console.log(`💡 Validation des tâches =>`, response)

    if (responseTypeIsError(response)) {
      yield call(manageErrorGenerator, { response, showMessageToUser: true })
    } else if (nonConformityType === "anomaly") {
      yield put(getFullAnomaliesAction())
    } else if (nonConformityType === "incident") {
      yield put(getFullIncidentsAction())
    } else if (nonConformityType === "auditRecommendation") {
      yield put(getFullAuditRecommendationsAction())
      if (auditPlanId) yield put(getAuditPlanByIdAction({ auditPlanId }))
    }
  } catch (err) {
    console.error(`[route] => /${endPath} => ERROR => `, err)
    yield put(setErrorMessage(err))
  }
}

// SPECIAL FOR ACTION PLAN THREAD
function* getActionPlanThreadByActionPlanIdSaga({ payload }) {
  const actionPlanId = payload
  const token = yield select(selectCurrentToken)
  const specificHeaders = { token: token }
  const { FORM_BACK_URL } = config
  const endPath = `actionPlanThreadByActionPlanId/${actionPlanId}`
  const requestURL = `${FORM_BACK_URL}/${endPath}`

  try {
    const response = yield call(request, requestURL, getCors(specificHeaders))
    if (responseTypeIsError(response))
      yield call(manageErrorGenerator, { response, showMessageToUser: true })
    else {
      if (responseTypeIsSuccess(response)) {
        yield call(manageSuccessGenerator, { response, showMessageToUser: false })
        yield put(getActionPlanThreadSuccessAction(response.body))
      }
    }
  } catch (err) {
    console.error(`[route] => /${endPath} => ERROR => `, err)
    yield put(setErrorMessage(err))
  }
}

function* postActionPlanMessageSaga({ payload }) {
  const { newMessage, actionPlanId } = payload
  const token = yield select(selectCurrentToken)
  const specificHeaders = { token: token }
  const { FORM_BACK_URL } = config
  const endPath = `addMessageToActionPlanThread`
  const requestURL = `${FORM_BACK_URL}/${endPath}`

  try {
    const response = yield call(
      request,
      requestURL,
      postCors({ newMessage, actionPlanId }, specificHeaders)
    )
    if (responseTypeIsError(response))
      yield call(manageErrorGenerator, { response, showMessageToUser: true })
    else {
      if (responseTypeIsSuccess(response)) {
        yield call(manageSuccessGenerator, { response, showMessageToUser: false })
        yield put(getActionPlanThreadAction(actionPlanId))
      }
    }
  } catch (err) {
    console.error(`[route] => /${endPath} => ERROR => `, err)
    yield put(setErrorMessage(err))
  }
}

function* actionPlanSaga() {
  yield takeEvery(GET_VALIDATE_ACTION_PLAN, getValidateActionPlanSaga)
  yield takeEvery(PATCH_VALIDATE_ACTION_PLAN, patchValidateActionPlanSaga)
  yield takeEvery(VALID_OR_REJECT_TASKS_ACTION_PLAN, validOrRejectTasksActionPlanSaga)
  // SPECIAL FOR ACTION PLAN THREAD
  yield takeEvery(GET_ACTION_PLAN_THREAD_BY_ACTION_PLAN_ID, getActionPlanThreadByActionPlanIdSaga)
  yield takeEvery(POST_MESSAGE_ACTION_PLAN_THREAD, postActionPlanMessageSaga)
}

export default actionPlanSaga
